import { INITIAL_AVATAR_MAX_RETRIES, RECONNECT_MAX_RETRIES } from '../constants';
import type { VoiceLiveContext } from './types';

/**
 * Guards for the Voice Live machine. Extracted from `voiceLiveMachine.ts`; since they live
 * outside `setup()`, `context` is annotated explicitly. Event data arrives via `params`,
 * wired at the transition, so no guard has to re-narrow the event union.
 */

/**
 * Whether a WebSocket close should trigger reconnection: reconnects are
 * permitted, the close code isn't terminal (1000/1001/4000/4001/4003), and
 * retries remain. A user-initiated stop flips `shouldReconnect` to false.
 */
export const shouldReconnect = (
  { context }: { context: VoiceLiveContext },
  { code }: { code: number },
) => {
  const isTerminalCode =
    code === 1000 || code === 1001 || code === 4000 || code === 4001 || code === 4003;
  return context.shouldReconnect && context.retryCount < RECONNECT_MAX_RETRIES && !isTerminalCode;
};

/**
 * Retry the avatar peer (vs. fall back) when it has never come up and the
 * initial-establishment retries aren't exhausted yet.
 */
export const canRetryAvatar = ({ context }: { context: VoiceLiveContext }) =>
  !context.avatarEverActive && context.initialAvatarFailures < INITIAL_AVATAR_MAX_RETRIES;

/** Escalate to audio-only once the initial avatar retries are exhausted. */
export const shouldEnterFallback = ({ context }: { context: VoiceLiveContext }) =>
  !context.avatarEverActive && context.initialAvatarFailures >= INITIAL_AVATAR_MAX_RETRIES;

/** Reconnect is still permitted — a user stop or terminal failure clears it. */
export const canReconnectSession = ({ context }: { context: VoiceLiveContext }) =>
  context.shouldReconnect;

/**
 * Switching to video only requires rebuilding the session when the socket itself was
 * negotiated audio-only; a video socket with the stream merely detached re-attaches in
 * place, so `isVideoEnabled` alone would be too broad here.
 */
export const isAudioOnlySession = ({ context }: { context: VoiceLiveContext }) =>
  context.socketIsAudioOnly;
